import i18n from "../locales/i18n";
import { addYears } from 'date-fns';
import DatesUtil from "./DatesUtil";
import { guidAt, measure } from "./trees/TreeChunk";
import { COLUMNS, NESTED, OVERRIDES, ROW } from "./trees/TreeSymbols";

export default class TreesUtil {
    static DEFAULT_DEATH_CAUSE_ID = 2; // "Autre(s) cause(s)"

    // Partagé par tous les arbres et gelé : ne jamais muter ces listes en place, les remplacer.
    static EMPTY_LIST = Object.freeze([]);

    // Omises par l'API quand elles sont vides (ShouldSerialize* de TreeProperties.cs).
    static LIST_PROPERTIES = [
        'coverTypeId', 'interactionId', 'microHabitatId',
        'rootSymptomId', 'rootPathogenId', 'rootPestId',
        'collarSymptomId', 'collarPathogenId', 'collarPestId',
        'trunkSymptomId', 'trunkPathogenId', 'trunkPestId', 'trunkEpiphyteId',
        'branchSymptomId', 'branchPathogenId', 'branchPestId', 'branchEpiphyteId',
        'leafSymptomId', 'leafPathogenId', 'leafPestId',
        'tagId'
    ];

    static INTERNED_PROPERTIES = ['category', 'place', 'creatorName', 'editorName'];

    static internPool = new Map();
    static INTERN_POOL_LIMIT = 5000;

    static areMemoryOptimizationsEnabled() {
        try {
            return localStorage.getItem('grality-phase1') !== 'off';
        } catch {
            return true;
        }
    }

    static intern(value) {
        if (typeof value !== 'string' || value.length === 0) return value;
        const existing = this.internPool.get(value);
        if (existing !== undefined) return existing;
        if (this.internPool.size >= this.INTERN_POOL_LIMIT) return value;
        this.internPool.set(value, value);
        return value;
    }

    static clearInternPool() {
        this.internPool.clear();
    }

    // Idempotente : peut être appelée à chaque point d'entrée.
    static normalizeTreeProperties(properties) {
        if (!properties) return properties;
        // Vue colonnaire : y écrire créerait une surcouche par arbre et annulerait le gain mémoire.
        if (properties.isColumnarView) return properties;

        const optimized = this.areMemoryOptimizationsEnabled();

        for (let i = 0; i < this.LIST_PROPERTIES.length; i++) {
            const name = this.LIST_PROPERTIES[i];
            const value = properties[name];
            if (value == null) properties[name] = optimized ? this.EMPTY_LIST : [];
            else if (optimized && Array.isArray(value) && value.length === 0) properties[name] = this.EMPTY_LIST;
        }

        if (optimized)
            for (let i = 0; i < this.INTERNED_PROPERTIES.length; i++) {
                const name = this.INTERNED_PROPERTIES[i];
                const value = properties[name];
                if (typeof value === 'string' && value.length > 0) properties[name] = this.intern(value);
            }

        return properties;
    }

    static normalizeTree(tree) {
        if (tree?.properties) this.normalizeTreeProperties(tree.properties);
        return tree;
    }

    static getStumpDiameters() {
        return [{ id: 1, label: '<30' }, { id: 2, label: '30-50' }, { id: 3, label: '50-100' },
        { id: 4, label: '100-150' }, { id: 5, label: '150-200' }, { id: 6, label: '>200' }];
    }

    static getCircumferenceByStumpDiameter(stumpDiameterId) {
        const stumpDiameters = this.getStumpDiameters();
        const stumpDiameterLabel = stumpDiameters[stumpDiameterId - 1].label;
        let min = 0, max = 0, diameter = 0;
        if (stumpDiameterLabel.includes('-')) {
            min = Number(stumpDiameterLabel.split('-')[0]);
            max = Number(stumpDiameterLabel.split('-')[1]);
            diameter = (min + max) / 2;
        } else if (stumpDiameterLabel.includes('<')) {
            max = Number(stumpDiameterLabel.replace('<', ''));
            diameter = (min + max) / 2;
        } else diameter = Number(stumpDiameterLabel.replace('>', ''));

        return Math.round(diameter * Math.PI);
    }

    static getStumpDiameterByCircumference(circumference) {
        if (!circumference) return null;
        const stumpDiameters = this.getStumpDiameters();
        circumference = Math.round(circumference / Math.PI);
        let stumpDiameter;
        for (let i = 0; i < stumpDiameters.length && !stumpDiameter; i++) {
            const diameter = stumpDiameters[i].label;
            let min = 0, max = 0;
            if (diameter.includes('-')) {
                min = Number(diameter.split('-')[0]);
                max = Number(diameter.split('-')[1]);
            } else if (diameter.includes('<')) max = Number(diameter.replace('<', ''));
            else min = Number(diameter.replace('>', ''));

            if ((circumference >= min && circumference <= max) || (max === 0 && circumference >= min)) stumpDiameter = stumpDiameters[i];
        }

        return stumpDiameter;
    }

    static getAgeIndex(age) {
        if (age === null || age === undefined || age === '' || isNaN(Number(age))) return 0;
        const tempAge = Number(age);
        return (tempAge === 0 ? 0 :
            tempAge >= 0 && tempAge <= 15 ? 1 :
                tempAge >= 16 && tempAge <= 50 ? 2 :
                    tempAge >= 51 && tempAge <= 100 ? 3 :
                        tempAge >= 101 && tempAge <= 200 ? 4 :
                            tempAge >= 201 && tempAge <= 300 ? 5 :
                                6);
    }

    static getFields(isPublicFields) {
        const treeOrgans = [{ organ: 'root' }, { organ: 'collar' }, { organ: 'trunk' }, { organ: 'branch' }, { organ: 'leaf' }];

        const fields = {
            projectReference: true,
            actions: true,
            longitude: true,
            latitude: true,
            vernacularName: true,
            gender: true,
            species: true,
            cultivar: true,
            creationInfos: true,
            modificationInfos: true,
            isDead: true,
            isEmpty: true,
            isStump: true,
            isChronoxyle: true,
            isIndexed: true,
            isRemarkable: true,
            isFruit: true,
            place: true,
            customReference: true,
            tags: true,
            plantationType: true,
            coverType: true,
            interactions: true,
            microHabitats: true,
            ...treeOrgans.reduce((prevValue, { organ }) => ({
                ...prevValue,
                [`${organ}Symptoms`]: isPublicFields,
                [`${organ}Pathogens`]: isPublicFields,
                [`${organ}Pests`]: isPublicFields,
                [`${organ}Epiphytes`]: ['trunk', 'branch'].includes(organ) && isPublicFields,
            }), {}),
            stumpDiameter: true,
            plantationCoefficient: true,
            situationCoefficient: true,
            patrimonialCoefficient: true,
            numberOfTrunks: true,
            trunkHeight: true,
            trunks: true,
            ontogenicStage: true,
            plantingDate: true,
            ageAtPlantation: true,
            age: true,
            deathCause: true,
            deathDate: true,
            vigor: true,
            risk: true,
            accurateRisk: false,
            treePort: true,
            healthReview: true,
            observation: true,
            toCutDown: true,
            archiStage: true,
            rootProtection: false
        };

        const formulas = {
            stability: true,
            canopy: true,
            carbonStock: true,
            coolingIndicator: true,
            oxygenProduction: true,
            biodiversityIndex: true,
            amenityValue: true
        }

        if (isPublicFields) {
            delete fields.projectReference;
            delete fields.customReference;
            delete fields.actions;
        } else {
            delete fields.canopy;
            delete fields.toCutDown;
            delete fields.isFruits;
            delete fields.stumpDiameter;
        }

        return { ...fields, ...formulas };
    }

    static getAccurateRiskLabel(accurateRisk) {
        return !accurateRisk ? accurateRisk :
            accurateRisk < 5 ? i18n.t("Très faible") :
                accurateRisk < 7 ? i18n.t("Faible") :
                    accurateRisk < 9 ? i18n.t("Moyen") :
                        accurateRisk < 11 ? i18n.t("Élevé") :
                            i18n.t("Très élevé");
    }

    static getStabilityLabel(stability) {
        return stability < 30 ? i18n.t("Très bon") :
            stability < 50 ? i18n.t("Bon") :
                stability < 70 ? i18n.t("Moyen") :
                    i18n.t("Mauvais");
    }

    static getSmallestTrunk(trunks) {
        return (trunks || []).reduce((accumulator, value) => {
            return (accumulator = typeof value.circumference !== 'number' || accumulator?.circumference < value.circumference ? accumulator : value);
        }, null);
    }

    static getBiggestTrunk(trunks) {
        if (!trunks?.length) return null;
        const biggest = (trunks || []).reduce((accumulator, value) => {
            return (accumulator = typeof value.circumference !== 'number' || accumulator?.circumference > value.circumference ? accumulator : value);
        }, null);
        return biggest || trunks[0];
    }

    static getBiggestTrunkValue(trunks, propertyName) {
        const biggestTrunk = this.getBiggestTrunk(trunks);
        if (!biggestTrunk) return null;
        return biggestTrunk[propertyName];
    }

    // null = chemin ordinaire : objet JS ordinaire ou troncs remplacés pendant la session.
    static getTrunkColumns(properties) {
        const trunks = properties?.[COLUMNS]?.trunks;
        if (!trunks) return null;

        const overrides = properties[OVERRIDES];
        if (overrides && 'trunks' in overrides) return null;

        return trunks;
    }

    static getBiggestTrunkRow(trunkColumns, row) {
        const start = trunkColumns.offsets[row], end = trunkColumns.offsets[row + 1];
        if (end === start) return -1;

        const circumferences = trunkColumns.circumference;
        let best = -1;
        for (let k = start; k < end; k++) {
            const circumference = circumferences[k];
            // NaN = mesure absente, écartée comme par getBiggestTrunk.
            if (Number.isNaN(circumference)) continue;
            // Strictement supérieur : le DERNIER ex æquo gagne, comme getBiggestTrunk. Ne pas simplifier.
            if (best !== -1 && circumferences[best] > circumference) continue;
            best = k;
        }

        // Comme `biggest || trunks[0]` : sans aucune circonférence, le premier tronc est retenu.
        return best === -1 ? start : best;
    }

    static getBiggestTrunkOf(properties) {
        const trunkColumns = this.getTrunkColumns(properties);
        if (!trunkColumns) return this.getBiggestTrunk(properties?.trunks);

        const index = this.getBiggestTrunkRow(trunkColumns, properties[ROW]);
        if (index < 0) return null;
        return this.trunkAt(properties, trunkColumns, index);
    }

    static trunkAt(properties, trunkColumns, index) {
        return {
            id: guidAt(trunkColumns.id, index * 16),
            height: measure(trunkColumns.height[index]),
            circumference: measure(trunkColumns.circumference[index]),
            crownDiameter: measure(trunkColumns.crownDiameter[index]),
            order: index - trunkColumns.offsets[properties[ROW]],
            propertiesId: properties.treeId,
            projectId: properties.projectId,
            fetchId: properties.fetchId
        };
    }

    // Pas de repli sur le premier tronc, contrairement à getBiggestTrunkRow (comme getSmallestTrunk).
    static getSmallestTrunkRow(trunkColumns, row) {
        const start = trunkColumns.offsets[row], end = trunkColumns.offsets[row + 1];
        const circumferences = trunkColumns.circumference;
        let best = -1;
        for (let k = start; k < end; k++) {
            const circumference = circumferences[k];
            if (Number.isNaN(circumference)) continue;
            if (best !== -1 && circumferences[best] < circumference) continue;
            best = k;
        }
        return best;
    }

    static getSmallestTrunkOf(properties) {
        const trunkColumns = this.getTrunkColumns(properties);
        if (!trunkColumns) return this.getSmallestTrunk(properties?.trunks);

        const index = this.getSmallestTrunkRow(trunkColumns, properties[ROW]);
        return index < 0 ? null : this.trunkAt(properties, trunkColumns, index);
    }

    // undefined (pas null) sans tronc : Number(null) vaut 0 et fausserait les filtres « = 0 ».
    static getBiggestTrunkValueOf(properties, propertyName) {
        const trunkColumns = this.getTrunkColumns(properties);
        if (!trunkColumns) return this.getBiggestTrunk(properties?.trunks)?.[propertyName];

        const index = this.getBiggestTrunkRow(trunkColumns, properties[ROW]);
        if (index < 0) return undefined;

        switch (propertyName) {
            case 'height': case 'circumference': case 'crownDiameter': return measure(trunkColumns[propertyName][index]);
            default: return this.getBiggestTrunkOf(properties)?.[propertyName];
        }
    }

    static getTrunkCount(properties) {
        const trunkColumns = this.getTrunkColumns(properties);
        if (!trunkColumns) return properties?.trunks?.length || 0;

        const row = properties[ROW];
        return trunkColumns.offsets[row + 1] - trunkColumns.offsets[row];
    }

    static eachTrunkCircumference(properties, visit) {
        const trunkColumns = this.getTrunkColumns(properties);
        if (!trunkColumns) {
            const trunks = properties?.trunks;
            if (!trunks) return;
            for (let index = 0; index < trunks.length; index++) {
                const circumference = trunks[index].circumference;
                if (circumference != null) visit(circumference);
            }
            return;
        }

        const row = properties[ROW];
        const circumferences = trunkColumns.circumference;
        for (let k = trunkColumns.offsets[row], end = trunkColumns.offsets[row + 1]; k < end; k++)
            if (!Number.isNaN(circumferences[k])) visit(circumferences[k]);
    }

    // Lit sans fabriquer dimensions/customFields, qui resteraient mémorisés sur chaque arbre.
    static getNestedColumns(properties, name) {
        const readers = properties?.[COLUMNS]?.[name];
        if (!readers) return null;
        // Déjà fabriqué : il fait autorité (il peut porter des écritures).
        if (properties[NESTED]?.[name]) return null;
        const overrides = properties[OVERRIDES];
        if (overrides && name in overrides) return null;
        return readers;
    }

    static getDimensionValue(properties, name) {
        const readers = this.getNestedColumns(properties, 'dimensions');
        if (!readers) return properties?.dimensions?.[name];

        const read = readers.get(name);
        return read === undefined ? undefined : read(properties[ROW]);
    }

    static getCustomFieldValue(properties, id) {
        const readers = this.getNestedColumns(properties, 'customFields');
        if (!readers) {
            // null = non renseigné, comme en colonnaire (sinon `valeur.split` plante au formatage).
            const value = properties?.customFields?.[id];
            return value === null ? undefined : value;
        }

        const read = readers.get(String(id));
        if (read === undefined) return undefined;
        const value = read(properties[ROW]);
        return value === null ? undefined : value;
    }

    static getCustomFieldIds(properties) {
        const readers = this.getNestedColumns(properties, 'customFields');
        if (!readers) {
            const customFields = properties?.customFields;
            if (!customFields) return this.EMPTY_LIST;
            return Object.keys(customFields).filter(id => customFields[id] !== null);
        }

        const row = properties[ROW];
        let ids = null;
        for (const [id, read] of readers)
            if (read(row) !== null) (ids || (ids = [])).push(id);
        return ids || this.EMPTY_LIST;
    }

    // Ne pas lire properties.observations avant ces tests : cela analyse tout le JSON.
    static getLatestObservationOf(properties) {
        const columns = properties?.[COLUMNS]?.observations;
        const overrides = properties?.[OVERRIDES];

        if (columns && !(overrides && 'observations' in overrides)) {
            const row = properties[ROW];
            const start = columns.offsets[row], end = columns.offsets[row + 1];
            if (end === start) return null;
            if (end - start === 1) return columns.read(start);

            const observations = new Array(end - start);
            for (let index = start; index < end; index++) observations[index - start] = columns.read(index);
            return TreesUtil.reduceToLatestObservation(observations);
        }

        return TreesUtil.reduceToLatestObservation(properties?.observations);
    }

    static reduceToLatestObservation(observations) {
        if (!Array.isArray(observations) || observations.length === 0) return null;
        return observations.reduce((latest, current) => {
            const latestTimestamp = new Date(latest.modificationDate || latest.creationDate).getTime();
            const currentTimestamp = new Date(current.modificationDate || current.creationDate).getTime();
            return currentTimestamp > latestTimestamp ? current : latest;
        });
    }

    // Ne pas lire properties.observations avant ces tests : cela analyse tout le JSON.
    static getObservationsText(properties) {
        const columns = properties?.[COLUMNS]?.observations;
        const overrides = properties?.[OVERRIDES];

        if (columns && !(overrides && 'observations' in overrides)) {
            const row = properties[ROW];
            const start = columns.offsets[row], end = columns.offsets[row + 1];
            if (end === start) return '';
            if (end - start === 1) {
                const value = columns.read(start).plainValue;
                return value === null || value === undefined ? '' : String(value);
            }

            const observations = new Array(end - start);
            for (let index = start; index < end; index++) observations[index - start] = columns.read(index);
            return TreesUtil.joinObservations(observations);
        }

        return TreesUtil.joinObservations(properties?.observations);
    }

    static joinObservations(observations) {
        return observations?.toSorted((a, b) => new Date(b.creationDate) - new Date(a.creationDate)).map(o => o.plainValue).join('') || '';
    }

    static getBiodiversityTooltip(properties) {
        if (!properties) return;
        const missingProperties = {};
        const biggestTrunk = this.getBiggestTrunk(properties.trunks);
        if (!properties.essenceId) missingProperties.essenceId = i18n.t("Essence");
        if (!properties.coverTypeId || !properties.coverTypeId.length) missingProperties.coverTypeId = i18n.t("Types de couverture au sol");
        if (!properties.situationCoefficientId) missingProperties.situationCoefficientId = i18n.t("Coefficient de situation");
        if (!properties.plantationCoefficientId) missingProperties.plantationCoefficientId = i18n.t("Coefficient de plantation");
        if (!biggestTrunk?.circumference && biggestTrunk?.circumference !== 0) missingProperties.circumference = i18n.t("Circonférence");

        if (Object.keys(missingProperties).length)
            return {
                tooltip: `${i18n.t("Le potentiel de biodiversité a été estimé car des données sont manquantes")} : ${Object.keys(missingProperties).map(property => missingProperties[property]?.toLowerCase()).join(', ')}`,
                isEstimated: true
            };
        else return { tooltip: i18n.t("Potentiel de valeur estimé pour la biodiversité de l'arbre, résultat adapté de publications scientifiques") }
    }

    static getToCutDown(projectAction, recurrence = null) {
        if (projectAction?.action?.id !== 1 || recurrence?.status === 'cancelled') return null;

        let date = recurrence?.date || projectAction.startDate;
        if (date && !(date instanceof Date))
            date = DatesUtil.convertUTCDateToDate(date);

        return recurrence?.status === 'done' ? 5
            : projectAction.isUrgent ? 4
                : date < addYears(new Date(), 1) ? 3
                    : date < addYears(new Date(), 3) ? 2 : 1;
    }

    static hasDeathInfo(properties) {
        return properties?.deathCauseId && properties?.deathDate ? true : false;
    }

    static getDefaultProperties() {
        return {
            category: 'Arbre',
            essenceId: 0,
            place: '',
            coverTypeId: [],
            interactionId: [],
            microHabitatId: [],
            rootSymptomId: [],
            rootPathogenId: [],
            rootPestId: [],
            collarSymptomId: [],
            collarPathogenId: [],
            collarPestId: [],
            trunkSymptomId: [],
            trunkPathogenId: [],
            trunkPestId: [],
            trunkEpiphyteId: [],
            branchSymptomId: [],
            branchPathogenId: [],
            branchPestId: [],
            branchEpiphyteId: [],
            leafSymptomId: [],
            leafPathogenId: [],
            leafPestId: [],
            tagId: [],
            vigorId: 0,
            deathCauseId: 0,
            deathDate: null,
            observations: [],
            healthReviewId: 0,
            plantingDate: null,
            ageAtPlantation: null,
            age: 0,
            customReference: null,
            ontogenicStageId: 0,
            archiStageId: 0,
            archiStateId: 0,
            riskId: 0,
            tippingRiskId: 0,
            organCaliberId: 0,
            targetId: 0,
            treePortId: 0,
            plantationTypeId: 0,
            numberOfTrunks: 0,
            trunks: [],
            plantationCoefficientId: 0,
            situationCoefficientId: 0,
            patrimonialCoefficientId: 0,
            isEmpty: false,
            isDead: false,
            isStump: false,
            isChronoxyle: false,
            isIndexed: false,
            isRemarkable: false,
            dimensions: {
                trunkHeight: 0,
            },
            customFields: {}
        };
    }
}

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') window.__gralityTreesUtil = TreesUtil;
